import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdPerson,
  MdTrendingUp,
  MdTrendingDown,
  MdCalendarToday,
  MdRefresh,
  MdDownload,
  MdVisibility,
  MdCheckCircle,
  MdWarning,
  MdDateRange,
  MdGroup,
  MdEmojiEvents,
  MdWorkOff,
  MdStar,
  MdSearch,
  MdFlag,
  MdQrCode2
} from 'react-icons/md';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';
import { QRCodeSVG } from 'qrcode.react';

const formatDateOnly = (date) => date.toISOString().split('T')[0];
const getWorkerNetWeight = (worker) => Number(worker.net_weight ?? worker.netWeight ?? worker.totalWeight ?? 0);
const getWorkerAverageOutput = (worker) => Number(worker.average_output ?? worker.averageOutput ?? 0);
const getWorkerEfficiency = (worker) => {
  if (typeof worker.currentEfficiency === 'number') {
    return worker.currentEfficiency;
  }
  const targetValue = Number(worker.target ?? 0);
  if (targetValue > 0) {
    return (getWorkerNetWeight(worker) / targetValue) * 100;
  }
  return 0;
};

const Performance = () => {
  const navigate = useNavigate();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [periodType, setPeriodType] = useState('daily'); // daily, monthly, yearly
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // for daily
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // for monthly (YYYY-MM)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString()); // for yearly
  const [rangeStartDate, setRangeStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 14);
    return formatDateOnly(start);
  });
  const [rangeEndDate, setRangeEndDate] = useState(() => formatDateOnly(new Date()));
  const [workerStatus, setWorkerStatus] = useState('all'); // all, current (3+ months), new (<3 months)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [newTargetValue, setNewTargetValue] = useState(0);
  const [qrModalData, setQrModalData] = useState(null);
  const qrCodeRef = useRef(null);
  const [isDownloadingQR, setIsDownloadingQR] = useState(false);
  const getWorkerProfileUrl = (workerId) => {
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://usibras-web.vercel.app';
    return `${origin}/admin/manualpeeling/worker/${workerId}`;
  };

  const handleOpenQRModal = (worker) => {
    const workerId = worker.worker_id || worker.id;
    const workerName = worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
    if (!workerId) {
      showToast('Unable to generate QR code: missing worker ID', 'error');
      return;
    }
    setQrModalData({
      workerId,
      workerName: workerName || 'Worker',
      group: worker.group_name || worker.group || 'Manual Peeling',
      cardNumber: worker.card_number || worker.id || 'N/A',
      url: getWorkerProfileUrl(workerId)
    });
  };

  const handleCloseQRModal = () => {
    setQrModalData(null);
    setIsDownloadingQR(false);
  };

  const handleDownloadWorkerQR = async () => {
    if (!qrModalData || !qrCodeRef.current) {
      showToast('QR code not ready yet', 'error');
      return;
    }
    const svgElement = qrCodeRef.current.querySelector('svg');
    if (!svgElement) {
      showToast('Unable to locate QR element', 'error');
      return;
    }
    try {
      setIsDownloadingQR(true);
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgElement);
      const encodedData = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(svgString)))}`;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngFile;
        downloadLink.download = `${qrModalData.workerId}-profile-qr.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setIsDownloadingQR(false);
        showToast('QR code downloaded', 'success');
      };
      img.onerror = () => {
        setIsDownloadingQR(false);
        showToast('Failed to render QR image', 'error');
      };
      img.src = encodedData;
    } catch (error) {
      setIsDownloadingQR(false);
      console.error('QR download error:', error);
      showToast('Unable to download QR code', 'error');
    }
  };

  // API data state
  const [apiData, setApiData] = useState(null);
  const [hasData, setHasData] = useState(true);

  // Fetch statistics from API
  const fetchStatistics = async () => {
    if (!['daily', 'monthly', 'range'].includes(periodType)) {
      return;
    }

    if (periodType === 'range') {
      if (!rangeStartDate || !rangeEndDate) {
        showToast('Please select both start and end dates.', 'error');
        return;
      }
      if (new Date(rangeStartDate) > new Date(rangeEndDate)) {
        showToast('Start date cannot be after end date.', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Build URL based on period type
      let url = `https://usibras-web-backend.onrender.com/api/manual-peeling/statistics?period_type=${periodType}&worker_status=${workerStatus}`;
      
      if (periodType === 'daily') {
        url += `&selected_date=${selectedDate}`;
      } else if (periodType === 'monthly') {
        url += `&selected_month=${selectedMonth}`;
      } else if (periodType === 'range') {
        url += `&start_date=${rangeStartDate}&end_date=${rangeEndDate}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok) {
        setApiData(result.data);
        setHasData(result.data && (result.data.worker_performance_details?.length > 0 || result.data.summary));
      } else {
        showToast(result.message || result.error || 'Failed to load statistics', 'error');
        setHasData(false);
        setApiData(null);
      }
    } catch (error) {
      showToast('Error fetching statistics: ' + error.message, 'error');
      console.error('Error:', error);
      setHasData(false);
      setApiData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when filters change (for daily and monthly)
  useEffect(() => {
    if (['daily', 'monthly', 'range'].includes(periodType)) {
      fetchStatistics();
    }
  }, [periodType, selectedDate, selectedMonth, rangeStartDate, rangeEndDate, workerStatus]);

  // Sample worker data - 5 workers representing different performance levels
  // Helper function to get dates relative to today
  const getRelativeDate = (daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  };

  const [workers, setWorkers] = useState([
    {
      id: 'MP001',
      name: 'Akosua Mensah',
      experience: '5 years',
      currentEfficiency: 94.2,
      avgEfficiency: 91.8,
      totalBatches: 156,
      totalWeight: 12450.5,
      grossWeight: 13500.0,
      netWeight: 12450.5,
      taredWeight: 1049.5,
      qualityGrade: 'A+',
      lastActive: getRelativeDate(0), // Today
      status: 'active',
      startDate: '2019-03-15',
      isCurrentWorker: true,
      dailyAverage: 4.2,
      target: 4.0,
      workingDays: 22,
      expectedWorkingDays: 26,
      weekendWorkingDays: 4,
      bonus: 150.00,
      group: 'Table 1',
      targetAchieved: true,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 1,
      absent3Consecutive: false,
      turnoverRisk: 'low',
      lastAbsentDate: getRelativeDate(7),
      weeklyPerformance: {
        batches: 12,
        weight: 950.5,
        efficiency: 94.2,
        quality: 'A+'
      },
      monthlyPerformance: {
        batches: 48,
        weight: 3850.2,
        efficiency: 92.1,
        quality: 'A'
      }
    },
    {
      id: 'MP004',
      name: 'Adwoa Asante',
      experience: '7 years',
      currentEfficiency: 96.8,
      avgEfficiency: 94.5,
      totalBatches: 203,
      totalWeight: 16890.2,
      grossWeight: 18200.0,
      netWeight: 16890.2,
      taredWeight: 1309.8,
      qualityGrade: 'A+',
      lastActive: getRelativeDate(1), // Yesterday
      status: 'active',
      tableNumber: 1,
      startDate: '2017-08-05',
      isCurrentWorker: true,
      dailyAverage: 5.1,
      target: 4.5,
      workingDays: 21,
      expectedWorkingDays: 26,
      weekendWorkingDays: 3,
      bonus: 200.00,
      group: 'Table 2',
      targetAchieved: true,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 0,
      absent3Consecutive: false,
      turnoverRisk: 'low',
      lastAbsentDate: null,
      weeklyPerformance: {
        batches: 15,
        weight: 1250.8,
        efficiency: 96.8,
        quality: 'A+'
      },
      monthlyPerformance: {
        batches: 58,
        weight: 4850.6,
        efficiency: 94.5,
        quality: 'A+'
      }
    },
    {
      id: 'MP006',
      name: 'Akua Frimpong',
      experience: '6 months',
      currentEfficiency: 75.4,
      avgEfficiency: 72.1,
      totalBatches: 23,
      totalWeight: 1680.9,
      grossWeight: 1850.0,
      netWeight: 1680.9,
      taredWeight: 169.1,
      qualityGrade: 'B',
      lastActive: getRelativeDate(3), // 3 days ago
      status: 'training',
      startDate: '2023-07-15',
      isCurrentWorker: true,
      dailyAverage: 2.8,
      target: 3.5,
      workingDays: 18,
      expectedWorkingDays: 26,
      weekendWorkingDays: 5,
      bonus: 50.00,
      group: 'Table 3',
      targetAchieved: false,
      absentToday: true,
      absentThisWeek: 2,
      absentThisMonth: 5,
      absent3Consecutive: true,
      turnoverRisk: 'high',
      lastAbsentDate: getRelativeDate(3),
      weeklyPerformance: {
        batches: 5,
        weight: 380.2,
        efficiency: 75.4,
        quality: 'B'
      },
      monthlyPerformance: {
        batches: 18,
        weight: 1280.5,
        efficiency: 72.1,
        quality: 'B-'
      }
    },
    {
      id: 'MP013',
      name: 'Kofi Asante',
      experience: '2 months',
      currentEfficiency: 68.5,
      avgEfficiency: 65.2,
      totalBatches: 12,
      totalWeight: 890.3,
      grossWeight: 980.0,
      netWeight: 890.3,
      taredWeight: 89.7,
      qualityGrade: 'C+',
      lastActive: getRelativeDate(0), // Today (to show mix of dates)
      status: 'training',
      startDate: '2025-08-15',
      isCurrentWorker: false, // NEW WORKER (< 3 months)
      dailyAverage: 2.1,
      target: 3.0,
      workingDays: 15,
      expectedWorkingDays: 26,
      weekendWorkingDays: 2,
      bonus: 0.00,
      group: 'Table 4',
      targetAchieved: false,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 2,
      absent3Consecutive: false,
      turnoverRisk: 'medium',
      lastAbsentDate: getRelativeDate(10),
      weeklyPerformance: {
        batches: 3,
        weight: 240.1,
        efficiency: 68.5,
        quality: 'C+'
      },
      monthlyPerformance: {
        batches: 12,
        weight: 890.3,
        efficiency: 65.2,
        quality: 'C'
      }
    },
    {
      id: 'MP014',
      name: 'Ama Ofori',
      experience: '1 month',
      currentEfficiency: 62.3,
      avgEfficiency: 58.9,
      totalBatches: 8,
      totalWeight: 580.7,
      grossWeight: 650.0,
      netWeight: 580.7,
      taredWeight: 69.3,
      qualityGrade: 'C',
      lastActive: getRelativeDate(2), // 2 days ago
      status: 'training',
      startDate: '2025-09-08',
      isCurrentWorker: false, // NEW WORKER (< 3 months)
      dailyAverage: 1.8,
      target: 2.5,
      workingDays: 12,
      expectedWorkingDays: 26,
      weekendWorkingDays: 1,
      bonus: 0.00,
      group: 'Table 5',
      targetAchieved: false,
      absentToday: true,
      absentThisWeek: 1,
      absentThisMonth: 3,
      absent3Consecutive: true,
      turnoverRisk: 'high',
      lastAbsentDate: getRelativeDate(2),
      weeklyPerformance: {
        batches: 2,
        weight: 160.2,
        efficiency: 62.3,
        quality: 'C'
      },
      monthlyPerformance: {
        batches: 8,
        weight: 580.7,
        efficiency: 58.9,
        quality: 'C-'
      }
    },
    {
      id: 'MP015',
      name: 'Kwame Boateng',
      experience: '4 years',
      currentEfficiency: 88.7,
      avgEfficiency: 87.3,
      totalBatches: 142,
      totalWeight: 11230.8,
      grossWeight: 12100.0,
      netWeight: 11230.8,
      taredWeight: 869.2,
      qualityGrade: 'A',
      lastActive: getRelativeDate(0), // Today
      status: 'active',
      startDate: '2020-05-20',
      isCurrentWorker: true,
      dailyAverage: 3.9,
      target: 3.8,
      workingDays: 24,
      expectedWorkingDays: 26,
      weekendWorkingDays: 2,
      bonus: 120.00,
      group: 'AT',
      targetAchieved: true,
      absentToday: false,
      absentThisWeek: 0,
      absentThisMonth: 0,
      absent3Consecutive: false,
      turnoverRisk: 'low',
      lastAbsentDate: getRelativeDate(15),
      weeklyPerformance: {
        batches: 10,
        weight: 820.5,
        efficiency: 88.7,
        quality: 'A'
      },
      monthlyPerformance: {
        batches: 42,
        weight: 3350.4,
        efficiency: 87.3,
        quality: 'A'
      }
    }
  ]);

  // Use API data for daily and monthly, fallback to sample data for yearly
  const getFilteredWorkers = () => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.worker_performance_details) {
      // Use API data for daily and monthly
      return apiData.worker_performance_details;
    }
    
    // Fallback to sample data for yearly
    let filtered = workers;

    // Filter by worker status
    if (workerStatus === 'current') {
      filtered = filtered.filter(w => w.isCurrentWorker);
    } else if (workerStatus === 'new') {
      filtered = filtered.filter(w => !w.isCurrentWorker);
    }

    if (periodType === 'monthly') {
      filtered = filtered.filter(w => {
        const lastActiveDate = new Date(w.lastActive).toISOString().slice(0, 7);
        return lastActiveDate === selectedMonth;
      });
    } else if (periodType === 'yearly') {
      filtered = filtered.filter(w => {
        const lastActiveYear = new Date(w.lastActive).getFullYear().toString();
        return lastActiveYear === selectedYear;
      });
    }

    return filtered;
  };

  const getStatusFilteredWorkers = () => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.worker_performance_details) {
      return apiData.worker_performance_details;
    }
    
    let filtered = workers;
    if (workerStatus === 'current') {
      filtered = filtered.filter(w => w.isCurrentWorker);
    } else if (workerStatus === 'new') {
      filtered = filtered.filter(w => !w.isCurrentWorker);
    }
    return filtered;
  };

  const filteredWorkers = getFilteredWorkers();
  const statusFilteredWorkers = getStatusFilteredWorkers();

  // Generate dynamic trend data based on selected period
  const generateTrendData = () => {
    const targetOutput = 1200.0;
    let data = [];

    if (periodType === 'daily') {
      // For daily view, show last 7 days including selected date
      const selectedDateObj = new Date(selectedDate);
      for (let i = 6; i >= 0; i--) {
        const date = new Date(selectedDateObj);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        // Calculate actual output from filtered workers
        const totalOutput = statusFilteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0) / 7 + (Math.random() * 200 - 100);
        const avgEfficiency = statusFilteredWorkers.length > 0 
          ? statusFilteredWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / statusFilteredWorkers.length
          : 0;
        
        data.push({
          date: dateStr,
          displayDate: date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
          actualOutput: Math.max(0, totalOutput),
          targetOutput: targetOutput,
          efficiency: Math.max(0, Math.min(100, avgEfficiency + (Math.random() * 10 - 5))),
          attendance: 85 + Math.random() * 15
        });
      }
    } else if (periodType === 'monthly') {
      // For monthly view, show weeks in the selected month
      const [year, month] = selectedMonth.split('-');
      const daysInMonth = new Date(year, month, 0).getDate();
      const weeksInMonth = Math.ceil(daysInMonth / 7);
      
      for (let week = 1; week <= weeksInMonth; week++) {
        const totalOutput = statusFilteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0) / weeksInMonth + (Math.random() * 500 - 250);
        const avgEfficiency = statusFilteredWorkers.length > 0 
          ? statusFilteredWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / statusFilteredWorkers.length
          : 0;
        
        data.push({
          date: `${year}-${month}-W${week}`,
          displayDate: `Week ${week}`,
          actualOutput: Math.max(0, totalOutput),
          targetOutput: targetOutput * 7,
          efficiency: Math.max(0, Math.min(100, avgEfficiency + (Math.random() * 10 - 5))),
          attendance: 85 + Math.random() * 15
        });
      }
    } else if (periodType === 'yearly') {
      // For yearly view, show all 12 months
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      months.forEach((monthName, index) => {
        const totalOutput = statusFilteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0) / 12 + (Math.random() * 2000 - 1000);
        const avgEfficiency = statusFilteredWorkers.length > 0 
          ? statusFilteredWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / statusFilteredWorkers.length
          : 0;
        
        data.push({
          date: `${selectedYear}-${String(index + 1).padStart(2, '0')}`,
          displayDate: monthName,
          actualOutput: Math.max(0, totalOutput),
          targetOutput: targetOutput * 30,
          efficiency: Math.max(0, Math.min(100, avgEfficiency + (Math.random() * 10 - 5))),
          attendance: 85 + Math.random() * 15
        });
      });
    } else if (periodType === 'range') {
      const start = new Date(rangeStartDate);
      const end = new Date(rangeEndDate);
      const totalDays = Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
      const cappedDays = Math.min(totalDays, 31);

      for (let i = 0; i < cappedDays; i++) {
        const current = new Date(start);
        current.setDate(start.getDate() + i);
        const perDayOutput = statusFilteredWorkers.reduce((sum, w) => {
          const workerDays = w.days_in_range || totalDays || 1;
          return sum + (getWorkerNetWeight(w) / workerDays);
        }, 0);
        const avgEfficiency = statusFilteredWorkers.length > 0 
          ? statusFilteredWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / statusFilteredWorkers.length
          : 0;
        
        data.push({
          date: formatDateOnly(current),
          displayDate: current.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
          actualOutput: Math.max(0, perDayOutput + (Math.random() * 200 - 100)),
          targetOutput: targetOutput,
          efficiency: Math.max(0, Math.min(100, avgEfficiency + (Math.random() * 10 - 5))),
          attendance: 85 + Math.random() * 15
        });
      }
    }

    return data;
  };

  const trendChartData = generateTrendData();

  // Calculate overall statistics
  const overallStats = (() => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.summary) {
      return {
        totalWorkers: apiData.summary.total_workers || 0,
        activeWorkers: apiData.summary.total_present || 0,
        avgEfficiency: apiData.summary.net_average ? Number(apiData.summary.net_average).toFixed(1) : 0,
        totalBatches: apiData.summary.total_batches || 0,
        totalWeight: apiData.summary.total_weight || 0,
        topPerformers: []
      };
    }
    
    // Fallback for other periods
    return {
      totalWorkers: statusFilteredWorkers.length,
      activeWorkers: statusFilteredWorkers.filter(w => w.status === 'active').length,
      avgEfficiency: statusFilteredWorkers.length > 0 ? (statusFilteredWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / statusFilteredWorkers.length).toFixed(1) : 0,
      totalBatches: statusFilteredWorkers.reduce((sum, w) => sum + (w.totalBatches || 0), 0),
      totalWeight: statusFilteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0).toFixed(1),
      topPerformers: statusFilteredWorkers.length > 0 ? (() => {
        const maxEfficiency = Math.max(...statusFilteredWorkers.map(w => getWorkerEfficiency(w)));
        return statusFilteredWorkers.filter(w => getWorkerEfficiency(w) === maxEfficiency);
      })() : []
    };
  })();

  // Calculate group analytics
  const groupAnalytics = (() => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.analytics) {
      const averageField = periodType === 'daily'
        ? 'daily_average'
        : periodType === 'monthly'
          ? 'monthly_average'
          : 'range_average';

      const averageValue = apiData.analytics[averageField];

      return {
        dailyAverage: averageValue !== undefined ? Number(averageValue).toFixed(1) : '0.0',
        workersAboveTarget: apiData.analytics.above_target || 0,
        workersUnderTarget: apiData.analytics.under_target || 0,
        targetAchievers: apiData.analytics.target_achievers || 0,
        absentToday: periodType === 'daily' ? (apiData.summary?.total_absent || 0) : 0,
        expectedWorkingDays: apiData.summary?.expected_working_days || 0,
        totalOutput: apiData.analytics.total_output?.toFixed(1) || '0.0'
      };
    }
    
    // Fallback for other periods
    const avgDailyForFiltered = filteredWorkers.length > 0 
      ? filteredWorkers.reduce((sum, w) => sum + (w.dailyAverage || 0), 0) / filteredWorkers.length
      : 0;
    
    return {
      dailyAverage: avgDailyForFiltered.toFixed(1),
      workersAboveTarget: filteredWorkers.filter(w => w.targetAchieved).length,
      workersUnderTarget: filteredWorkers.filter(w => !w.targetAchieved).length,
      targetAchievers: filteredWorkers.filter(w => w.targetAchieved).length,
      absentToday: statusFilteredWorkers.filter(w => w.absentToday).length,
      absentThisWeek: filteredWorkers.reduce((sum, w) => sum + (w.absentThisWeek || 0), 0),
      absentThisMonth: filteredWorkers.reduce((sum, w) => sum + (w.absentThisMonth || 0), 0),
      highTurnoverRisk: statusFilteredWorkers.filter(w => w.turnoverRisk === 'high').length,
      mediumTurnoverRisk: statusFilteredWorkers.filter(w => w.turnoverRisk === 'medium').length,
      lowTurnoverRisk: statusFilteredWorkers.filter(w => w.turnoverRisk === 'low').length,
      totalOutput: filteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0).toFixed(1)
    };
  })();

  // Get top 5 and bottom 5 performers
  const topPerformers = (() => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.analytics?.top_5_performers) {
      return apiData.analytics.top_5_performers;
    }
    const sortedByAverageOutput = [...filteredWorkers].sort((a, b) => getWorkerAverageOutput(b) - getWorkerAverageOutput(a));
    return sortedByAverageOutput.slice(0, Math.min(5, sortedByAverageOutput.length));
  })();

  const bottomPerformers = (() => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.analytics?.bottom_5_performers) {
      return apiData.analytics.bottom_5_performers;
    }
    const sortedByAverageOutput = [...filteredWorkers].sort((a, b) => getWorkerAverageOutput(b) - getWorkerAverageOutput(a));
    return sortedByAverageOutput.length > 5 
      ? [...sortedByAverageOutput].slice(-5).reverse() 
      : [...sortedByAverageOutput].reverse();
  })();

  // Calculate group statistics
  const groupStats = (() => {
    if (['daily', 'monthly', 'range'].includes(periodType) && apiData?.group_performance) {
      return apiData.group_performance.map(group => {
        const normalizedOutput = Number(group.total_output || 0);
        const normalizedTarget = Number(group.group_target || 0);
        const presentWorkers = group.present_workers
          ?? group.total_present
          ?? group.active_workers
          ?? group.total_workers;
        // For range period type, use average_output_per_present from API if available
        const avgOutput = periodType === 'range' && group.average_output_per_present !== undefined
          ? Number(group.average_output_per_present)
          : (presentWorkers > 0 ? normalizedOutput / presentWorkers : 0);

        return {
          name: group.group_name,
          workers: group.total_workers,
          avgOutput,
          avgEfficiency: group.achievement_percentage || 0,
          totalOutput: normalizedOutput,
          groupTarget: normalizedTarget,
          averageDailyGroupAverage: periodType === 'range' && group.average_daily_group_average !== undefined
            ? Number(group.average_daily_group_average)
            : null,
          topWorker: null
        };
      });
    }
    
    // Fallback for other periods
    const groups = ['Table 1', 'Table 2', 'Table 3', 'Table 4', 'Table 5', 'AT'];
    return groups.map(groupName => {
      const groupWorkers = workers.filter(w => w.group === groupName);
      // Consider only present workers when in daily view and the flag exists
      const presentGroupWorkers = (periodType === 'daily')
        ? groupWorkers.filter(w => !w.absentToday)
        : groupWorkers;
      const presentCount = presentGroupWorkers.length;
      const presentTotalOutput = presentGroupWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0);
      const avgOutput = presentCount > 0 ? (presentTotalOutput / presentCount) : 0;
      const avgEfficiency = groupWorkers.length > 0 
        ? (groupWorkers.reduce((sum, w) => sum + getWorkerEfficiency(w), 0) / groupWorkers.length)
        : 0;
      const totalOutput = groupWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0);
      const groupTarget = groupWorkers.reduce((sum, w) => sum + (w.target || 0), 0);
      
      return {
        name: groupName,
        workers: groupWorkers.length,
        avgOutput: avgOutput,
        avgEfficiency: avgEfficiency,
        totalOutput: totalOutput,
        groupTarget: groupTarget,
        averageDailyGroupAverage: null, // Only available for range period type
        topWorker: groupWorkers.length > 0 ? groupWorkers.sort((a, b) => getWorkerNetWeight(b) - getWorkerNetWeight(a))[0] : null
      };
    });
  })();

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  // Sort filtered workers by total weight (descending) - consistent with top/bottom performers
  // Also apply search filter
  const sortedWorkers = [...filteredWorkers]
    .filter(w => w.name.toLowerCase().includes(searchName.toLowerCase()))
    .sort((a, b) => {
      const aWeight = getWorkerNetWeight(a);
      const bWeight = getWorkerNetWeight(b);
      return bWeight - aWeight;
    });

  // Pagination logic
  const totalPages = Math.ceil(sortedWorkers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWorkers = sortedWorkers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [periodType, selectedDate, selectedMonth, selectedYear, workerStatus, searchName, rangeStartDate, rangeEndDate]);

  // Get performance color based on efficiency
  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 90) return 'text-green-600 bg-green-100';
    if (efficiency >= 80) return 'text-yellow-600 bg-yellow-100';
    if (efficiency >= 70) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  // Get quality grade color
  const getQualityColor = (grade) => {
    if (grade.includes('A+')) return 'text-green-600 bg-green-100';
    if (grade.includes('A')) return 'text-blue-600 bg-blue-100';
    if (grade.includes('B')) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'training': return 'text-blue-600 bg-blue-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (['daily', 'monthly', 'range'].includes(periodType)) {
      fetchStatistics();
      showToast('Performance data refreshed successfully', 'success');
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        showToast('Performance data refreshed successfully', 'success');
      }, 1000);
    }
  };

  // Handle export
  const toCsv = (rows) => {
    if (!rows || rows.length === 0) return '';
    const headers = Object.keys(rows[0]);
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const lines = [headers.join(',')];
    for (const row of rows) {
      lines.push(headers.map(h => escape(row[h])).join(','));
    }
    return lines.join('\n');
  };

  const downloadCsv = (filename, csvText) => {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildReportRows = () => {
    const source = (periodType === 'daily' || periodType === 'monthly') && apiData?.worker_performance_details
      ? apiData.worker_performance_details
      : filteredWorkers;

    return (source || []).map((w) => {
      const name = w.name || `${w.first_name || ''} ${w.last_name || ''}`.trim();
      const cardNumber = w.card_number || w.id || w.worker_id;
      const workStatus = w.work_status || (w.isCurrentWorker ? 'Current' : 'New');
      const groupName = w.group_name || w.group;
      const netWeight = getWorkerNetWeight(w);
      const target = w.target ?? 0;
      const deviationFromTarget = w.deviation_from_target !== undefined
        ? w.deviation_from_target
        : ((w.dailyAverage ?? 0) - (w.target ?? 0));
      const groupAverage = w.group_average ?? 0;
      const deviationFromGroupAvg = w.deviation_from_group_average !== undefined
        ? w.deviation_from_group_average
        : (netWeight - groupAverage);
      const expectedWorkingDays = w.expected_working_days ?? w.expectedWorkingDays ?? '';
      const actualWorkingDays = w.actual_working_days ?? w.workingDays ?? '';
      const weekendDaysWorked = w.weekend_days_worked ?? w.weekendWorkingDays ?? '';
      const daysInRange = w.days_in_range ?? '';
      const bonusAmount = w.bonus_amount ?? w.bonus ?? '';

      return {
        name,
        card_number: cardNumber,
        work_status: workStatus,
        group_name: groupName,
        net_weight: Number(netWeight).toFixed(1),
        target: Number(target).toFixed(1),
        deviation_from_target: Number(deviationFromTarget).toFixed(1),
        deviation_from_group_average: Number(deviationFromGroupAvg).toFixed(1),
        expected_working_days: expectedWorkingDays,
        actual_working_days: actualWorkingDays,
        days_in_range: daysInRange,
        weekend_days_worked: weekendDaysWorked,
        bonus_amount: bonusAmount === '' ? '' : Number(bonusAmount).toFixed(2),
      };
    });
  };

  const handleExport = () => {
    try {
      const rows = buildReportRows();
      const csv = toCsv(rows);
      const suffix = periodType === 'daily' 
        ? selectedDate 
        : periodType === 'monthly' 
          ? selectedMonth 
          : periodType === 'range'
            ? `${rangeStartDate}_to_${rangeEndDate}`
            : selectedYear;
      const filename = `manual_peeling_report_${periodType}_${suffix}.csv`;
      downloadCsv(filename, csv);
      showToast('CSV exported successfully', 'success');
    } catch (err) {
      showToast('Failed to export CSV', 'error');
    }
  };

  // Handle open target modal
  const handleOpenTargetModal = (worker) => {
    setSelectedWorker(worker);
    setNewTargetValue(worker.target);
    setShowTargetModal(true);
  };

  // Handle close target modal
  const handleCloseTargetModal = () => {
    setShowTargetModal(false);
    setSelectedWorker(null);
    setNewTargetValue(0);
  };

  // Handle save target
  const handleSaveTarget = async () => {
    if (!selectedWorker) return;

    if (newTargetValue <= 0) {
      showToast('Please enter a valid target value greater than 0', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Calculate target change
      const targetChange = newTargetValue - selectedWorker.target;

      // Prepare request body
      const requestBody = {
        worker_id: selectedWorker.id,
        worker_name: selectedWorker.name,
        worker_group: selectedWorker.group,
        target_value: parseFloat(newTargetValue),
        previous_target: parseFloat(selectedWorker.target),
        target_change: parseFloat(targetChange)
      };

      // Send to API
      const response = await fetch('https://usibras-web-backend.onrender.com/api/single-manual-targets', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state with new target
        const updatedWorkers = workers.map(w => 
          w.id === selectedWorker.id 
            ? { ...w, target: newTargetValue }
            : w
        );
        setWorkers(updatedWorkers);
        
        showToast(data.message || `Target updated to ${newTargetValue.toFixed(1)} kg for ${selectedWorker.name}`, 'success');
        handleCloseTargetModal();
      } else {
        showToast(data.message || data.error || 'Failed to update target', 'error');
      }
    } catch (error) {
      showToast('Error updating target: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/manualpeeling')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <MdArrowBack className="text-xl" />
              <span>Back to Manual Peeling</span>
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Worker Performance</h1>
        </div>
        <p className="text-gray-600 mb-6">
          Monitor and analyze manual peeling worker performance metrics
        </p>
        
        {/* Main Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Period Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MdCalendarToday className="inline mr-2 text-blue-500" />
                View Period
              </label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                <option value="daily">Daily View</option>
                <option value="monthly">Monthly View</option>
                <option value="range">Date Range</option>
              </select>
            </div>

            {/* Dynamic Date Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MdDateRange className="inline mr-2 text-green-500" />
                {periodType === 'daily' && 'Select Date'}
                {periodType === 'monthly' && 'Select Month'}
                {periodType === 'yearly' && 'Select Year'}
                {periodType === 'range' && 'Select Date Range'}
              </label>
              {periodType === 'daily' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                />
              )}
              {periodType === 'monthly' && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                />
              )}
              {periodType === 'yearly' && (
                <input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  min="2020"
                  max="2030"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                />
              )}
              {periodType === 'range' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="block text-xs font-semibold text-gray-600 mb-1">Start Date</span>
                    <input
                      type="date"
                      value={rangeStartDate}
                      max={rangeEndDate}
                      onChange={(e) => setRangeStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                    />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-600 mb-1">End Date</span>
                    <input
                      type="date"
                      value={rangeEndDate}
                      min={rangeStartDate}
                      onChange={(e) => setRangeEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Worker Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MdGroup className="inline mr-2 text-purple-500" />
                Worker Status
              </label>
              <select
                value={workerStatus}
                onChange={(e) => setWorkerStatus(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                <option value="all">All</option>
                <option value="current">Current</option>
                <option value="new">New Workers</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleRefresh}
              className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center font-medium"
            >
              <MdRefresh className="mr-2" />
              Refresh Data
            </button>
            <button
              onClick={handleExport}
              className="px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center font-medium"
            >
              <MdDownload className="mr-2" />
              Export Report
            </button>
          </div>
        </div>

        {/* Current Filter Display */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MdCalendarToday className="text-blue-600 mr-2 text-xl" />
              <div>
                <span className="text-sm font-medium text-gray-700">Currently Viewing: </span>
                <span className="text-base font-bold text-blue-800">
                  {periodType === 'daily' && new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                  {periodType === 'monthly' && new Date(selectedMonth + '-01').toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long' 
                  })}
                  {periodType === 'yearly' && selectedYear}
                  {periodType === 'range' && (
                    `${new Date(rangeStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(rangeEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  )}
                </span>
                <span className="text-sm text-gray-600 ml-2">
                  ({workerStatus === 'all' ? 'All Workers' : workerStatus === 'current' ? 'Current Workers' : 'New Workers'})
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {filteredWorkers.length} worker{filteredWorkers.length !== 1 ? 's' : ''} found
              {searchName && sortedWorkers.length !== filteredWorkers.length && (
                <span className="ml-2 text-blue-600">
                  ({sortedWorkers.length} match search)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* No Data Message */}
      {(['daily', 'monthly', 'range'].includes(periodType)) && !hasData && !isLoading && (
        <div className="bg-white rounded-xl shadow-lg p-12 mb-8">
          <div className="flex flex-col items-center justify-center text-center">
            <MdWarning className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
            <p className="text-gray-500 mb-4">
              No performance data found for the selected {periodType === 'daily' ? 'date' : periodType === 'monthly' ? 'month' : 'date range'} and filters.
            </p>
            <button
              onClick={handleRefresh}
              className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center font-medium"
            >
              <MdRefresh className="mr-2" />
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      {((!['daily', 'monthly', 'range'].includes(periodType)) || hasData) && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Workers</p>
              <p className="text-3xl font-bold text-gray-900">{overallStats.totalWorkers}</p>
              {/* <p className="text-sm text-green-600 flex items-center mt-1">
                <MdTrendingUp className="mr-1" />
                {overallStats.activeWorkers} active
              </p> */}
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <MdPerson className="text-2xl text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Absent</p>
              {periodType === 'daily' ? (
                <p className="text-3xl font-bold text-gray-900">{groupAnalytics.absentToday}</p>
              ) : (
                <p className="text-lg font-medium text-gray-500 italic mt-2">Data not applicable</p>
              )}
              {/* <p className="text-sm text-red-600 flex items-center mt-1">
                <MdWorkOff className="mr-1" />
               
               </p> */}
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <MdWorkOff className="text-2xl text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Average</p>
              <p className="text-3xl font-bold text-gray-900">
                {(['daily', 'monthly', 'range'].includes(periodType)) && apiData?.summary?.net_average
                  ? Number(apiData.summary.net_average).toFixed(1)
                  : filteredWorkers.length > 0 
                    ? (filteredWorkers.reduce((sum, w) => sum + getWorkerNetWeight(w), 0) / filteredWorkers.length).toFixed(1) 
                    : '0.0'} kg
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <MdTrendingUp className="text-2xl text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Present</p>
              {periodType === 'daily' ? (
                <p className="text-3xl font-bold text-gray-900">{overallStats.totalWorkers - groupAnalytics.absentToday}</p>
              ) : (
                <p className="text-lg font-medium text-gray-500 italic mt-2">Data not applicable</p>
              )}
              {/* <p className="text-sm text-green-600 flex items-center mt-1">
                <MdCheckCircle className="mr-1" />
                Out of {overallStats.totalWorkers} workers
               </p> */}
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <MdCheckCircle className="text-2xl text-green-600" />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Absent Workers Card */}
      {periodType === 'daily' && groupAnalytics.absentToday > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdWorkOff className="mr-2 text-red-500" />
            Absent Workers Today ({groupAnalytics.absentToday})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(apiData?.absent_workers || []).map((worker) => (
              <div key={worker.card_number} className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <MdPerson className="text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{worker.first_name} {worker.last_name}</p>
                      <p className="text-sm text-gray-500">{worker.card_number}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    worker.work_status === 'Current' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {worker.work_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Performers Highlight */}
      {/* Top Performers Highlight commented out */}
      {false && (
        <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-xl shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">
                {overallStats.topPerformers.length === 1 ? 'Top Performer This Week' : `Top Performers This Week (${overallStats.topPerformers.length})`}
              </h3>
              <div className="flex items-center">
                <MdStar className="text-yellow-300 mr-1" />
                <span className="font-semibold">{overallStats.topPerformers[0].currentEfficiency}% Efficiency</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{overallStats.topPerformers[0].currentEfficiency}%</div>
              <p className="text-sm opacity-90">Peeling Efficiency</p>
            </div>
          </div>
          
          {/* Commented out detailed top performers list as requested */}
          {false && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {overallStats.topPerformers.map((worker, index) => (
                <div key={worker.id} className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-400 text-yellow-900 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{worker.name}</p>
                        <p className="text-sm opacity-90">{worker.id}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="opacity-90">Daily Average:</span>
                      <span className="font-medium">{worker.dailyAverage} kg</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="opacity-90">Status:</span>
                      <span className={`font-medium ${worker.isCurrentWorker ? 'text-green-300' : 'text-yellow-300'}`}>
                        {worker.isCurrentWorker ? 'Current' : 'New'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Group Performance Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <MdGroup className="mr-2 text-purple-500 text-2xl" />
            Table Performance Overview
          </h3>
          <span className="text-sm text-gray-600">
            {groupStats.filter(g => g.workers > 0).length} Active Groups
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {groupStats.map((group, index) => (
            <div 
              key={group.name}
              className={`relative overflow-hidden rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                group.workers > 0 
                  ? 'bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200' 
                  : 'bg-gray-50 border-2 border-gray-200 opacity-60'
              }`}
            >
              {/* Group Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                    group.name === 'AT' ? 'text-sm' : 'text-lg'
                  } ${
                    group.workers > 0 
                      ? group.name === 'AT'
                        ? 'bg-gradient-to-br from-green-500 to-teal-600 text-white shadow-lg'
                        : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {group.name === 'AT' ? 'AT' : index + 1}
                  </div>
                  <div className="ml-3">
                    <h4 className="font-bold text-gray-800 text-base">{group.name}</h4>
                    <p className="text-xs text-gray-600">{group.workers} worker{group.workers !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>

              {/* Group Stats */}
              {group.workers > 0 ? (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Average Output</p>
                    <p className="text-lg font-bold text-purple-600">{group.avgOutput.toFixed(1)} kg</p>
                  </div>
                  
                  {periodType === 'range' && group.averageDailyGroupAverage !== null && (
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-gray-600 mb-1">Group Average of Average</p>
                      <p className="text-lg font-bold text-blue-600">{group.averageDailyGroupAverage.toFixed(1)} kg</p>
                    </div>
                  )}
                  
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Total Output</p>
                    <p className="text-lg font-bold text-green-600">{group.totalOutput.toFixed(1)} kg</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 italic">No workers assigned</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Group Comparison Chart */}
        <div className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-blue-100">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdTrendingUp className="mr-2 text-blue-500" />
            Table Performance Comparison (Average Output)
          </h4>
          <div className="space-y-3">
            {groupStats
              .filter(g => g.workers > 0)
              .sort((a, b) => b.avgOutput - a.avgOutput)
              .map((group, index) => {
                const maxAvgOutput = Math.max(...groupStats.map(g => g.avgOutput));
                const percentage = maxAvgOutput > 0 ? (group.avgOutput / maxAvgOutput) * 100 : 0;
                
                return (
                  <div key={group.name} className="flex items-center">
                    <div className="w-24 font-medium text-sm text-gray-700">{group.name}</div>
                    <div className="flex-1 mx-4">
                      <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden shadow-inner">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 flex items-center justify-end px-3 ${
                            group.name === 'AT'
                              ? 'bg-gradient-to-r from-teal-400 to-teal-600'
                              : index === 0 
                                ? 'bg-gradient-to-r from-green-400 to-green-600' 
                                : index === 1 
                                  ? 'bg-gradient-to-r from-blue-400 to-blue-600' 
                                  : 'bg-gradient-to-r from-purple-400 to-purple-600'
                          }`}
                          style={{ width: `${percentage}%` }}
                        >
                          {percentage > 15 && (
                            <span className="text-white font-semibold text-sm">
                              {group.avgOutput.toFixed(1)} kg
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Group Average of Average Comparison Chart - Only for Range */}
        {periodType === 'range' && (
          <div className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-blue-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <MdTrendingUp className="mr-2 text-blue-500" />
              Table Performance Comparison (Group Average of Average)
            </h4>
            <div className="space-y-3">
              {groupStats
                .filter(g => g.workers > 0 && g.averageDailyGroupAverage !== null)
                .sort((a, b) => (b.averageDailyGroupAverage || 0) - (a.averageDailyGroupAverage || 0))
                .map((group, index) => {
                  const maxAvgDailyGroupAvg = Math.max(...groupStats
                    .filter(g => g.averageDailyGroupAverage !== null)
                    .map(g => g.averageDailyGroupAverage || 0));
                  const percentage = maxAvgDailyGroupAvg > 0 
                    ? ((group.averageDailyGroupAverage || 0) / maxAvgDailyGroupAvg) * 100 
                    : 0;
                  
                  return (
                    <div key={group.name} className="flex items-center">
                      <div className="w-24 font-medium text-sm text-gray-700">{group.name}</div>
                      <div className="flex-1 mx-4">
                        <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden shadow-inner">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 flex items-center justify-end px-3 ${
                              group.name === 'AT'
                                ? 'bg-gradient-to-r from-teal-400 to-teal-600'
                                : index === 0 
                                  ? 'bg-gradient-to-r from-green-400 to-green-600' 
                                  : index === 1 
                                    ? 'bg-gradient-to-r from-blue-400 to-blue-600' 
                                    : 'bg-gradient-to-r from-purple-400 to-purple-600'
                            }`}
                            style={{ width: `${percentage}%` }}
                          >
                            {percentage > 15 && (
                              <span className="text-white font-semibold text-sm">
                                {group.averageDailyGroupAverage?.toFixed(1)} kg
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>


      {/* Group Analytics */}
      {((!['daily', 'monthly', 'range'].includes(periodType)) || hasData) && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdGroup className="mr-2 text-blue-500" />
            Analytics
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {periodType === 'monthly' ? 'Monthly Average' : periodType === 'range' ? 'Range Average' : 'Daily Average'}
              </span>
              {periodType === 'daily' && apiData?.analytics?.daily_average === undefined ? (
                <span className="text-sm font-medium text-gray-500 italic">N/A</span>
              ) : (
                <span className="font-semibold text-gray-900">{groupAnalytics.dailyAverage} kg</span>
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Above Target</span>
              <span className="font-semibold text-green-600">{groupAnalytics.workersAboveTarget}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Under Target</span>
              <span className="font-semibold text-red-600">{groupAnalytics.workersUnderTarget}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Target Achievers</span>
              <span className="font-semibold text-green-600">{groupAnalytics.targetAchievers}</span>
            </div>
            {groupAnalytics.expectedWorkingDays && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Expected Days</span>
                <span className="font-semibold text-gray-900">{groupAnalytics.expectedWorkingDays}</span>
              </div>
            )}
            <div className="flex justify-between items-center border-t pt-3">
              <span className="text-sm font-medium text-gray-700">Total Output</span>
              <span className="font-bold text-blue-600">{groupAnalytics.totalOutput} kg</span>
            </div>
          </div>
        </div>

        {/* Top 5 Performers */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdEmojiEvents className="mr-2 text-yellow-500" />
            Top 5 Performers (by Average Output)
          </h3>
          {topPerformers.length > 0 ? (
            <div className="space-y-3">
              {topPerformers.map((worker, index) => (
                <div key={worker.card_number || worker.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-green-600">{getWorkerAverageOutput(worker).toFixed(1)} kg</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No workers found for selected filters</p>
          )}
        </div>

        {/* Bottom 5 Performers */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MdTrendingDown className="mr-2 text-red-500" />
            Bottom 5 Performers (by Average Output)
          </h3>
          {bottomPerformers.length > 0 ? (
            <>
              {/* {filteredWorkers.length <= 5 && (
                <p className="text-xs text-amber-600 mb-3 bg-amber-50 p-2 rounded">
                  Note: With {filteredWorkers.length} worker{filteredWorkers.length !== 1 ? 's' : ''}, all workers are shown in both lists
                </p>
              )} */}
              <div className="space-y-3">
                {bottomPerformers.map((worker, index) => (
                  <div key={worker.card_number || worker.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-red-600">{getWorkerAverageOutput(worker).toFixed(1)} kg</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 italic">No workers found for selected filters</p>
          )}
        </div>
      </div>

      )}

      {/* Workers Table */}
      {((!['daily', 'monthly', 'range'].includes(periodType)) || hasData) && (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Worker Performance Details</h2>
            <div className="relative w-64">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchName && (
                <button
                  onClick={() => setSearchName('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Worker
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Card Number
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Work Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Group Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Net Weight
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Deviation from Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Deviation from Group Average
                </th>
                {(periodType === 'daily' || periodType === 'range') && (
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Average Output
                </th>
                )}
                {periodType !== 'daily' && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Expected Working Days
                  </th>
                )}
                {periodType !== 'daily' && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Actual Working Days
                  </th>
                )}
                {periodType === 'range' && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Days in Range
                  </th>
                )}
                {periodType !== 'daily' && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Weekend Working Days
                  </th>
                )}
                {periodType === 'monthly' && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                    Bonus
                  </th>
                )}
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedWorkers.length === 0 ? (
                <tr>
                  <td colSpan="18" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <MdSearch className="text-6xl text-gray-300 mb-4" />
                      <p className="text-lg font-medium text-gray-600 mb-2">No workers found</p>
                      <p className="text-sm text-gray-500">
                        {searchName ? `No workers match "${searchName}"` : 'Try adjusting your filters'}
                      </p>
                      {searchName && (
                        <button
                          onClick={() => setSearchName('')}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedWorkers.map((worker) => {
                  const workerId = worker.worker_id || worker.id;
                  const workerName = worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim() || 'Worker';
                  const cardNumber = worker.card_number || worker.id;
                  const workStatus = worker.work_status || (worker.isCurrentWorker ? 'Current' : 'New');
                  const groupName = worker.group_name || worker.group;
                  const netWeight = getWorkerNetWeight(worker);
                  const target = Number(worker.target || 0);
                  const deviationFromTarget = worker.deviation_from_target !== undefined 
                    ? worker.deviation_from_target 
                    : (worker.dailyAverage ? worker.dailyAverage - target : 0);
                  const groupAverage = worker.group_average || (groupStats.find(g => g.name === groupName)?.avgOutput) || 0;
                  const deviationFromGroupAvg = worker.deviation_from_group_average !== undefined
                    ? worker.deviation_from_group_average
                    : (netWeight - groupAverage);
                  const daysInRangeValue = worker.days_in_range !== undefined
                    ? worker.days_in_range
                    : (periodType === 'range' ? apiData?.summary?.expected_working_days : undefined);
                  
                  return (
                    <tr key={workerId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <MdPerson className="text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{workerName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cardNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          workStatus === 'Current' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {workStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded">
                          {groupName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {netWeight.toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {target.toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          deviationFromTarget >= 0 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                        }`}>
                          {deviationFromTarget >= 0 ? '+' : ''}{deviationFromTarget.toFixed(1)} kg
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          deviationFromGroupAvg >= 0 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                        }`}>
                          {deviationFromGroupAvg >= 0 ? '+' : ''}{deviationFromGroupAvg.toFixed(1)} kg
                        </span>
                      </td>
                  {(periodType === 'daily' || periodType === 'range') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getWorkerAverageOutput(worker).toFixed(1)} kg
                    </td>
                  )}
                  {periodType !== 'daily' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {worker.expected_working_days || worker.expectedWorkingDays || 'N/A'}
                    </td>
                  )}
                  {periodType !== 'daily' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {worker.actual_working_days || worker.workingDays || 'N/A'}
                    </td>
                  )}
                  {periodType === 'range' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {daysInRangeValue !== undefined ? daysInRangeValue : 'N/A'}
                    </td>
                  )}
                  {periodType !== 'daily' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded">
                        {worker.weekend_days_worked || worker.weekendWorkingDays || 0}
                      </span>
                    </td>
                  )}
                  {periodType === 'monthly' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold ${
                        (worker.bonus_amount || worker.bonus || 0) > 0 ? 'text-green-700' : 'text-gray-600'
                      }`}>
                        GH₵ {(worker.bonus_amount || worker.bonus || 0).toFixed(2)}
                      </span>
                    </td>
                  )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => navigate(`/admin/manualpeeling/worker/${workerId}`)}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-1"
                          >
                            <MdVisibility className="text-lg" />
                            <span>View</span>
                          </button>
                          <button 
                            onClick={() => handleOpenTargetModal(worker)}
                            className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-1"
                          >
                            <MdFlag className="text-lg" />
                            <span>Set Target</span>
                          </button>
                          <button
                            onClick={() => handleOpenQRModal(worker)}
                            className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-1"
                          >
                            <MdQrCode2 className="text-lg" />
                            <span>QR Code</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedWorkers.length)} of {sortedWorkers.length} workers
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Worker QR Modal */}
      {qrModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-white/80 mb-1">Worker QR Code</p>
                <h3 className="text-2xl font-semibold">{qrModalData.workerName}</h3>
                <p className="text-sm text-white/80 mt-1">{qrModalData.cardNumber}</p>
              </div>
              <button
                onClick={handleCloseQRModal}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 flex flex-col items-center text-center space-y-4">
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200" ref={qrCodeRef}>
                <QRCodeSVG
                  value={qrModalData.url}
                  size={220}
                  bgColor="#ffffff"
                  fgColor="#111827"
                  includeMargin
                />
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <p><span className="font-semibold text-gray-800">Profile URL:</span> {qrModalData.url}</p>
                <p><span className="font-semibold text-gray-800">Table:</span> {qrModalData.group}</p>
              </div>
              <div className="flex flex-col sm:flex-row w-full gap-3">
                <button
                  onClick={() => navigator.clipboard?.writeText(qrModalData.url)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Copy Profile Link
                </button>
                <button
                  onClick={handleDownloadWorkerQR}
                  disabled={isDownloadingQR}
                  className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isDownloadingQR ? 'Preparing...' : 'Download QR'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Target Modal */}
      {showTargetModal && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold flex items-center">
                  <MdFlag className="mr-2 text-2xl" />
                  Set Target
                </h3>
                <button
                  onClick={handleCloseTargetModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Worker Info */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <MdPerson className="text-2xl text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedWorker.name}</p>
                    <p className="text-sm text-gray-600">{selectedWorker.id}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Current Target:</span>
                    <span className="ml-2 font-semibold text-gray-900">{selectedWorker.target} kg</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Group:</span>
                    <span className="ml-2 font-semibold text-purple-600">{selectedWorker.group}</span>
                  </div>
                </div>
              </div>

              {/* Target Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Target Value (kg)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={newTargetValue}
                    onChange={(e) => setNewTargetValue(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg font-semibold pr-12"
                    placeholder="Enter new target"
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                    kg
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Enter the daily output target for this worker
                </p>
              </div>

              {/* Comparison */}
              <div className="mb-6 bg-blue-50 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Target Change Preview</p>
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-gray-600">Previous:</span>
                    <span className="ml-2 font-semibold text-gray-900">{selectedWorker.target.toFixed(1)} kg</span>
                  </div>
                  <div className="text-2xl text-gray-400">→</div>
                  <div>
                    <span className="text-gray-600">New:</span>
                    <span className="ml-2 font-semibold text-green-600">{newTargetValue.toFixed(1)} kg</span>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-gray-600">Change:</span>
                  <span className={`ml-2 font-semibold ${
                    newTargetValue - selectedWorker.target >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {newTargetValue - selectedWorker.target >= 0 ? '+' : ''}{(newTargetValue - selectedWorker.target).toFixed(1)} kg
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCloseTargetModal}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTarget}
                  disabled={isLoading || newTargetValue <= 0}
                  className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium flex items-center justify-center disabled:bg-green-300 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <MdCheckCircle className="mr-2" />
                      Save Target
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default Performance;
